/**
 * Table - 데이터 테이블 컴포넌트
 *
 * 정렬, 행 선택, 페이지네이션, 빈 상태, 로딩 상태를 지원하는 범용 테이블.
 * AvatarCell, ProgressCell, TagCell, ActionCell, ActionMenu 등 셀 헬퍼도 함께 제공.
 * @param columns - 컬럼 정의 배열
 * @param data - 행 데이터 배열
 * @param selectable - true이면 체크박스 행 선택 기능 활성화
 */
'use client';

import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react';
import Avatar from './Avatar';
import Badge from './Badge';
import EmptyState from './EmptyState';
import LoadingSpinner from './LoadingSpinner';
import styles from './Table.module.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface TableColumn<T> {
  /** data[key] 접근 키 (또는 render만 사용 시 임의 문자열) */
  key: string;
  /** 헤더에 표시할 제목 */
  title: string;
  /** 커스텀 셀 렌더 */
  render?: (value: unknown, record: T, index: number) => React.ReactNode;
  /** 컬럼 너비 (CSS 값) */
  width?: string | number;
  /** 텍스트 정렬 */
  align?: 'left' | 'center' | 'right';
  /** 정렬 가능 여부 */
  sortable?: boolean;
}

export interface TablePagination {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}

export interface TableProps<T> {
  /** 컬럼 정의 */
  columns: TableColumn<T>[];
  /** 데이터 배열 */
  data: T[];
  /** 각 행의 고유 키 */
  rowKey: string | ((record: T) => string);
  /** 로딩 상태 */
  loading?: boolean;
  /** 빈 상태 제목 */
  emptyTitle?: string;
  /** 빈 상태 설명 */
  emptyDescription?: string;
  /** 빈 상태 아이콘 */
  emptyIcon?: React.ReactNode;
  /** 행 선택 기능 */
  selectable?: boolean;
  /** 선택된 키 배열 */
  selectedKeys?: string[];
  /** 선택 변경 콜백 */
  onSelectionChange?: (keys: string[]) => void;
  /** 현재 정렬 키 */
  sortKey?: string;
  /** 정렬 방향 */
  sortDirection?: 'asc' | 'desc';
  /** 정렬 변경 콜백 */
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  /** 행 클릭 콜백 */
  onRowClick?: (record: T) => void;
  /** 페이지네이션 */
  pagination?: TablePagination;
  /** 추가 className */
  className?: string;
  /** 컴팩트 모드 */
  compact?: boolean;
  /** 줄무늬 행 */
  striped?: boolean;
  /** 테두리 표시 */
  bordered?: boolean;
  /** 스티키 헤더 */
  stickyHeader?: boolean;
  /** 카드 스타일 (border + shadow + rounded) */
  elevated?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function getRowKey<T>(record: T, rowKey: string | ((record: T) => string)): string {
  if (typeof rowKey === 'function') return rowKey(record);
  return String(getNestedValue(record as unknown as Record<string, unknown>, rowKey) ?? '');
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

export interface AvatarCellProps {
  src?: string;
  name: string;
  subtitle?: string;
}

export function AvatarCell({ src, name, subtitle }: AvatarCellProps) {
  return (
    <div className={styles.avatarCell}>
      <Avatar src={src} name={name} size="sm" />
      <div className={styles.avatarCellText}>
        <span className={styles.avatarCellName}>{name}</span>
        {subtitle && <span className={styles.avatarCellSubtitle}>{subtitle}</span>}
      </div>
    </div>
  );
}

export interface ProgressCellProps {
  value: number;
  max?: number;
  color?: string;
}

export function ProgressCell({ value, max = 100, color }: ProgressCellProps) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className={styles.progressCell}>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className={styles.progressLabel}>{Math.round(pct)}%</span>
    </div>
  );
}

export interface TagCellProps {
  tags: string[];
}

export function TagCell({ tags }: TagCellProps) {
  return (
    <div className={styles.tagCell}>
      {tags.map((tag) => (
        <Badge key={tag} variant="default" size="sm">
          {tag}
        </Badge>
      ))}
    </div>
  );
}

export interface ActionCellAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

export interface ActionCellProps {
  actions: ActionCellAction[];
}

export function ActionCell({ actions }: ActionCellProps) {
  return (
    <div className={styles.actionCell}>
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          className={`${styles.actionBtn} ${action.variant === 'danger' ? styles.actionBtnDanger : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
          }}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  StackedCell                                                        */
/* ------------------------------------------------------------------ */

export interface StackedCellProps {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  mono?: boolean;
}

export function StackedCell({ primary, secondary, mono = false }: StackedCellProps) {
  return (
    <div className={styles.stackedCell}>
      <span className={`${styles.stackedPrimary} ${mono ? styles.stackedMono : ''}`}>
        {primary}
      </span>
      {secondary && (
        <span className={`${styles.stackedSecondary} ${mono ? styles.stackedMono : ''}`}>
          {secondary}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ActionMenu                                                         */
/* ------------------------------------------------------------------ */

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  icon?: React.ReactNode;
}

export interface ActionMenuProps {
  primary?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'secondary';
  };
  items: ActionMenuItem[];
}

export function ActionMenu({ primary, items }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (items.length === 0 && !primary) return null;

  return (
    <div className={styles.actionMenu} onClick={(e) => e.stopPropagation()}>
      {primary && (
        <button
          type="button"
          className={`${styles.actionBtn} ${primary.variant === 'danger' ? styles.actionBtnDanger : ''}`}
          onClick={primary.onClick}
        >
          {primary.label}
        </button>
      )}
      {items.length > 0 && (
        <div className={styles.actionMenuWrapper} ref={wrapperRef}>
          <button
            type="button"
            className={styles.actionMenuTrigger}
            onClick={() => setOpen((prev) => !prev)}
            aria-label="추가 액션"
          >
            <MoreVertical size={16} />
          </button>
          {open && (
            <div className={styles.actionMenuDropdown}>
              {items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={`${styles.actionMenuItem} ${item.variant === 'danger' ? styles.actionMenuItemDanger : ''}`}
                  onClick={() => {
                    item.onClick();
                    setOpen(false);
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Table<T>({
  columns,
  data,
  rowKey,
  loading = false,
  emptyTitle = '데이터가 없습니다',
  emptyDescription = '표시할 항목이 없습니다.',
  emptyIcon,
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
  sortKey,
  sortDirection = 'asc',
  onSort,
  onRowClick,
  pagination,
  className,
  compact = false,
  striped = false,
  bordered = false,
  stickyHeader = false,
  elevated = false,
}: TableProps<T>) {
  /* ---- Selection ---- */
  const allKeys = useMemo(() => data.map((r) => getRowKey(r, rowKey)), [data, rowKey]);

  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.includes(k));
  const someSelected = !allSelected && allKeys.some((k) => selectedKeys.includes(k));

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(selectedKeys.filter((k) => !allKeys.includes(k)));
    } else {
      const merged = new Set([...selectedKeys, ...allKeys]);
      onSelectionChange(Array.from(merged));
    }
  }, [allSelected, allKeys, selectedKeys, onSelectionChange]);

  const handleSelectRow = useCallback(
    (key: string) => {
      if (!onSelectionChange) return;
      if (selectedKeys.includes(key)) {
        onSelectionChange(selectedKeys.filter((k) => k !== key));
      } else {
        onSelectionChange([...selectedKeys, key]);
      }
    },
    [selectedKeys, onSelectionChange],
  );

  /* ---- Sort ---- */
  const handleSort = useCallback(
    (key: string) => {
      if (!onSort) return;
      const nextDir = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(key, nextDir);
    },
    [sortKey, sortDirection, onSort],
  );

  /* ---- Pagination ---- */
  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize))
    : 1;

  const pageNumbers = useMemo(() => {
    if (!pagination) return [];
    const pages: number[] = [];
    const current = pagination.page;
    const maxVisible = 5;
    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [pagination, totalPages]);

  /* ---- Container classes ---- */
  const wrapCls = [
    styles.wrapper,
    compact && styles.compact,
    striped && styles.striped,
    bordered && styles.bordered,
    stickyHeader && styles.stickyHeader,
    elevated && styles.elevated,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  /* ---- Render ---- */
  return (
    <div className={wrapCls}>
      <div className={styles.scrollArea}>
        <table className={styles.table}>
          {/* ---- Header ---- */}
          <thead className={styles.thead}>
            <tr>
              {selectable && (
                <th className={`${styles.th} ${styles.checkboxCell}`}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={handleSelectAll}
                    aria-label="전체 선택"
                  />
                </th>
              )}
              {columns.map((col) => {
                const isSorted = sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    className={`${styles.th} ${col.sortable ? styles.sortable : ''} ${isSorted ? styles.sorted : ''}`}
                    style={{
                      width: col.width,
                      textAlign: col.align ?? 'left',
                    }}
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    aria-sort={
                      isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                  >
                    <span className={styles.thContent}>
                      {col.title}
                      {col.sortable && (
                        <span className={styles.sortIcons}>
                          <ChevronUp
                            size={12}
                            className={`${styles.sortIcon} ${isSorted && sortDirection === 'asc' ? styles.sortActive : ''}`}
                          />
                          <ChevronDown
                            size={12}
                            className={`${styles.sortIcon} ${isSorted && sortDirection === 'desc' ? styles.sortActive : ''}`}
                          />
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* ---- Body ---- */}
          <tbody className={styles.tbody}>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className={styles.loadingCell}>
                  <LoadingSpinner size="md" />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className={styles.emptyCell}>
                  <EmptyState
                    icon={
                      emptyIcon ?? (
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ opacity: 0.5 }}
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                          <line x1="9" y1="21" x2="9" y2="9" />
                        </svg>
                      )
                    }
                    title={emptyTitle}
                    description={emptyDescription}
                  />
                </td>
              </tr>
            ) : (
              data.map((record, rowIndex) => {
                const key = getRowKey(record, rowKey);
                const isSelected = selectedKeys.includes(key);
                return (
                  <tr
                    key={key}
                    className={`${styles.tr} ${isSelected ? styles.selectedRow : ''} ${onRowClick ? styles.clickableRow : ''}`}
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? 'link' : undefined}
                    onClick={onRowClick ? () => onRowClick(record) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowClick(record);
                            }
                          }
                        : undefined
                    }
                  >
                    {selectable && (
                      <td className={`${styles.td} ${styles.checkboxCell}`}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectRow(key);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`행 ${rowIndex + 1} 선택`}
                        />
                      </td>
                    )}
                    {columns.map((col) => {
                      const value = getNestedValue(
                        record as unknown as Record<string, unknown>,
                        col.key,
                      );
                      return (
                        <td
                          key={col.key}
                          className={styles.td}
                          style={{ textAlign: col.align ?? 'left' }}
                        >
                          {col.render
                            ? col.render(value, record, rowIndex)
                            : ((value as React.ReactNode) ?? '-')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ---- Pagination ---- */}
      {pagination && data.length > 0 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            총 {pagination.total}건 중 {(pagination.page - 1) * pagination.pageSize + 1}-
            {Math.min(pagination.page * pagination.pageSize, pagination.total)}건
          </span>
          <div className={styles.paginationButtons}>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={pagination.page <= 1}
              onClick={() => pagination.onChange(pagination.page - 1)}
              aria-label="이전 페이지"
            >
              <ChevronLeft size={16} />
            </button>
            {pageNumbers.map((num) => (
              <button
                key={num}
                type="button"
                className={`${styles.pageBtn} ${pagination.page === num ? styles.pageBtnActive : ''}`}
                onClick={() => pagination.onChange(num)}
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              className={styles.pageBtn}
              disabled={pagination.page >= totalPages}
              onClick={() => pagination.onChange(pagination.page + 1)}
              aria-label="다음 페이지"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
